import { PriceChange } from "@crypto-trading/common/browser";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExchangeCode } from "@/lib/market-data/market-data.types";
import {
  CandleFetchErrorMeta,
  CandleInterval,
  CandlePoint,
  toPriceChange,
  toPublicChartErrorMessage,
} from "@/lib/live-candles/candle.types";
import { CANDLE_INTERVAL_MS } from "@/lib/live-candles/candle.types";
import {
  getPriceMarkets,
  getPriceServiceRest,
  getPriceStream,
  isStreamedInterval,
  MAX_CANDLES_PER_REQUEST,
  FUTURES_PRICE_PRIORITY,
  SPOT_PRICE_PRIORITY,
  toFuturesServiceExchange,
  toServiceInterval,
  toServiceSymbol,
  toSpotServiceExchange,
  type ServiceExchange,
  type ServiceMarket,
} from "@/lib/prices";
import {
  candleWindowRequest,
  isRateLimitError,
  resolveCandleSource,
  toCandlePoint,
} from "@/lib/prices/candles";
import {
  candleCacheKey,
  getCachedCandles,
  markCandlesEmpty,
  setCachedCandles,
} from "@/lib/prices/candle-cache";

const DEFAULT_PAGE_LIMIT = 500;
const DEFAULT_FOCUS_CANDLES = 60;
/** Intervals the stream does not push (4h) are refreshed over REST this often. */
const LIVE_POLL_MS = 20_000;

/** A price service market a chart reads candles from. */
export interface CandleSource {
  exchange: ServiceExchange;
  symbol: string;
}

/**
 * Candidate venues as price service markets, keeping the caller's order. The
 * market list supplies each venue's own symbol; unless `trustCandidates`,
 * venues that do not list the token are skipped. With `anyVenue` (alert
 * charts, where showing a chart matters more than where it comes from) every
 * futures venue that lists the token follows the candidates (priced venues
 * first). The sources are tried in order, two at a time. A token no
 * futures venue lists charts from spot (the candidates' spot markets first,
 * then the spot priority order), like its live price.
 */
function toCandleSources(
  candidates: ExchangeCode[],
  symbol: string,
  markets: ServiceMarket[] | null,
  trustCandidates: boolean,
  anyVenue: boolean
): CandleSource[] {
  const fallbackSymbol = toServiceSymbol(symbol);
  const sources: CandleSource[] = [];
  for (const candidate of candidates) {
    const exchange = toFuturesServiceExchange(candidate);
    if (!exchange || sources.some((source) => source.exchange === exchange)) {
      continue;
    }
    const listed = markets?.find((market) => market.exchange === exchange);
    if (!listed && markets && !trustCandidates) continue;
    sources.push({ exchange, symbol: listed?.symbol ?? fallbackSymbol });
  }
  if (anyVenue && markets) {
    // Priced venues first: one without a price is unlikely to have candles.
    for (const priced of [true, false]) {
      for (const exchange of FUTURES_PRICE_PRIORITY) {
        if (sources.some((source) => source.exchange === exchange)) continue;
        const listed = markets.find(
          (market) => market.exchange === exchange && market.priced === priced
        );
        if (listed) sources.push({ exchange, symbol: listed.symbol });
      }
    }
  }
  if (!markets || sources.some((source) => markets.some((m) => m.exchange === source.exchange))) {
    return sources;
  }
  const spot = [
    ...candidates.map(toSpotServiceExchange),
    ...SPOT_PRICE_PRIORITY,
  ];
  for (const exchange of spot) {
    if (!exchange || sources.some((source) => source.exchange === exchange)) {
      continue;
    }
    const listed = markets.find((market) => market.exchange === exchange);
    if (listed) sources.push({ exchange, symbol: listed.symbol });
  }
  return sources;
}

interface UseChartCandlesParams {
  userId: string;
  symbol: string;
  exchange: ExchangeCode;
  /** Tried in order when the preferred exchange has no candles. */
  exchanges?: ExchangeCode[];
  interval: CandleInterval;
  startTime: number | null;
  endTime: number | null;
  enabled?: boolean;
  pageLimit?: number;
  /** Extra candles to include before startTime on the initial fetch. */
  paddingCandles?: number;
  /** Alert charts retry the latest candles when the alert window is still empty. */
  allowRecentFallback?: boolean;
  /** Candidates were already chosen. Skip the live availability lookup. */
  trustCandidates?: boolean;
  /** Any venue with candles will do (alert charts); the trade drawer keeps its own exchange. */
  anyVenue?: boolean;
  /** Initial fetch window centered on this timestamp (open/trigger). */
  focusAnchorTime?: number | null;
  /** Initial fetch spans startTime through this timestamp (closed positions). */
  focusEndTime?: number | null;
  /** Visible candle count used to size the focusAnchorTime window. */
  initialFocusCandles?: number;
  /**
   * Live chart of this exact market (trade drawer): loads the latest candles
   * and follows the stream from the start, with no venue lookup or fallback.
   * History is reused from the in-memory candle cache; startTime and endTime
   * are not used.
   */
  liveMarket?: CandleSource | null;
}

interface KeptInterval {
  candles: CandlePoint[];
  source: CandleSource;
  hasMoreHistory: boolean;
}

interface UseChartCandlesResult {
  priceChanges: PriceChange[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: CandleFetchErrorMeta | null;
  loadOlderCandles: () => Promise<void>;
  loadNewerCandles: () => Promise<void>;
  hasMoreHistory: boolean;
  hasMoreFuture: boolean;
  setFollowLive: (follow: boolean) => void;
  /** No exchange has candles because the token is not listed yet. */
  historicalUnavailable: boolean;
}

function mapError(error: unknown): CandleFetchErrorMeta {
  const raw = error instanceof Error ? error.message : "Unknown candle error";
  const message = toPublicChartErrorMessage(raw);
  const lower = raw.toLowerCase();
  if (lower.includes("not available") || lower.includes("not_found")) {
    return { code: "TOKEN_NOT_AVAILABLE", message };
  }
  if (lower.includes("429") || lower.includes("rate limit") || lower.includes("too many")) {
    return { code: "RATE_LIMIT", message: "Rate limited — try again in a moment" };
  }
  if (lower.includes("history") || lower.includes("retention")) {
    return { code: "HISTORY_OUT_OF_RANGE", message };
  }
  if (lower.includes("network") || lower.includes("fetch") || lower.includes("request failed")) {
    return { code: "NETWORK", message };
  }
  return { code: "UNKNOWN", message };
}

function mergeCandles(existing: CandlePoint[], incoming: CandlePoint[]): CandlePoint[] {
  const map = new Map<number, CandlePoint>();
  existing.forEach((candle) => map.set(candle.startTime, candle));
  incoming.forEach((candle) => map.set(candle.startTime, candle));
  return Array.from(map.values()).sort((a, b) => a.startTime - b.startTime);
}

function getInitialFetchRange(params: {
  startTime: number;
  endTime: number;
  interval: CandleInterval;
  paddingCandles: number;
  focusAnchorTime?: number | null;
  focusEndTime?: number | null;
  initialFocusCandles: number;
}): { fetchStartTime: number; fetchEndTime: number } {
  const intervalMs = CANDLE_INTERVAL_MS[params.interval];
  const paddingMs = params.paddingCandles * intervalMs;
  const now = Date.now();

  if (params.focusEndTime != null) {
    return {
      fetchStartTime: params.startTime - paddingMs,
      fetchEndTime: params.focusEndTime + paddingMs,
    };
  }

  if (params.focusAnchorTime != null) {
    const visible = params.initialFocusCandles;
    const barsBefore = Math.floor(visible * 0.35);
    const barsAfter = visible - barsBefore;
    return {
      fetchStartTime: params.focusAnchorTime - barsBefore * intervalMs - paddingMs,
      fetchEndTime: Math.min(
        params.focusAnchorTime + barsAfter * intervalMs + paddingMs,
        now
      ),
    };
  }

  return {
    fetchStartTime: params.startTime - paddingMs,
    fetchEndTime: Math.min(params.endTime, now),
  };
}

export function useChartCandles({
  userId,
  symbol,
  exchange,
  exchanges,
  interval,
  startTime,
  endTime,
  enabled = true,
  pageLimit = DEFAULT_PAGE_LIMIT,
  paddingCandles = 10,
  allowRecentFallback = false,
  trustCandidates = false,
  anyVenue = false,
  focusAnchorTime = null,
  focusEndTime = null,
  initialFocusCandles = DEFAULT_FOCUS_CANDLES,
  liveMarket = null,
}: UseChartCandlesParams): UseChartCandlesResult {
  const [candles, setCandles] = useState<CandlePoint[]>([]);
  /** Interval the candles in state belong to. */
  const [candlesInterval, setCandlesInterval] = useState<CandleInterval | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<CandleFetchErrorMeta | null>(null);
  const [historicalUnavailable, setHistoricalUnavailable] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [hasMoreFuture, setHasMoreFuture] = useState(true);
  const [followLive, setFollowLive] = useState(false);
  const [source, setSource] = useState<CandleSource | null>(null);
  const isMountedRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const earliestStartRef = useRef<number | null>(null);
  const latestStartRef = useRef<number | null>(null);
  const sourceRef = useRef<CandleSource | null>(null);
  const fetchGenerationRef = useRef(0);
  /** Cache key of the candles in state (live charts), null while switching. */
  const loadedCacheKeyRef = useRef<string | null>(null);
  /**
   * Window charts (positions, alerts): each interval's candles while the chart
   * stays open, so going back to an interval shows what the user scrolled
   * through instead of refetching. Cleared when the chart closes or shows
   * something else.
   */
  const keptIntervalsRef = useRef<{
    key: string;
    byInterval: Map<CandleInterval, KeptInterval>;
  }>({ key: "", byInterval: new Map() });
  /** Interval of the window candles in state, null while switching. */
  const loadedIntervalRef = useRef<CandleInterval | null>(null);

  const exchangeCandidates = useMemo(() => {
    if (exchanges && exchanges.length > 0) return exchanges;
    return [exchange];
  }, [exchange, exchanges]);
  const exchangeCandidatesKey = exchangeCandidates.join(",");
  const exchangeCandidatesRef = useRef(exchangeCandidates);
  exchangeCandidatesRef.current = exchangeCandidates;

  const pageSize = Math.min(pageLimit, MAX_CANDLES_PER_REQUEST);

  const liveExchange = liveMarket?.exchange ?? null;
  const liveSymbol = liveMarket?.symbol ?? null;

  const shouldEnable =
    enabled &&
    Boolean(userId) &&
    Boolean(symbol) &&
    (Boolean(liveExchange && liveSymbol) ||
      (Boolean(startTime) && Boolean(endTime)));

  const applyCandles = useCallback((incoming: CandlePoint[]) => {
    if (incoming.length === 0) return;
    setCandles((previous) => {
      const merged = mergeCandles(previous, incoming);
      earliestStartRef.current = merged[0]?.startTime ?? null;
      latestStartRef.current = merged[merged.length - 1]?.startTime ?? null;
      return merged;
    });
  }, []);

  /** Live charts: cached or latest candles of one market, then follow live. */
  const fetchLive = useCallback(
    async (market: CandleSource, isCurrent: () => boolean) => {
      const intervalMs = CANDLE_INTERVAL_MS[interval];
      const serviceInterval = toServiceInterval(interval);
      const key = candleCacheKey(market.exchange, market.symbol, serviceInterval);
      const rest = getPriceServiceRest();

      sourceRef.current = market;
      setSource(market);
      setError(null);
      setHistoricalUnavailable(false);
      setHasMoreFuture(false);

      const showCandles = (loaded: CandlePoint[], moreHistory: boolean) => {
        loadedCacheKeyRef.current = key;
        earliestStartRef.current = loaded[0]?.startTime ?? null;
        latestStartRef.current = loaded[loaded.length - 1]?.startTime ?? null;
        setCandles(loaded);
        setCandlesInterval(interval);
        setHasMoreHistory(moreHistory);
        setFollowLive(true);
      };

      const cached = getCachedCandles(key);
      if (cached) {
        // Shown at once; only the candles since it was last current are fetched.
        showCandles(cached.candles, cached.hasMoreHistory);
        setIsLoading(false);
        const lastStart = cached.candles[cached.candles.length - 1].startTime;
        try {
          const gap = await rest.getCandles({
            exchange: market.exchange,
            symbol: market.symbol,
            interval: serviceInterval,
            ...candleWindowRequest(lastStart, Date.now(), intervalMs),
          });
          if (isCurrent()) applyCandles(gap.map(toCandlePoint));
        } catch {
          // The stream still keeps the latest candle current.
        }
        return;
      }

      setIsLoading(true);
      setCandles([]);
      setHasMoreHistory(true);
      setFollowLive(false);
      try {
        const fetched = await rest.getCandles({
          exchange: market.exchange,
          symbol: market.symbol,
          interval: serviceInterval,
          limit: pageSize,
        });
        if (!isCurrent()) return;
        if (fetched.length === 0) {
          markCandlesEmpty(key);
          setHasMoreHistory(false);
          setHistoricalUnavailable(true);
          return;
        }
        showCandles(fetched.map(toCandlePoint), true);
      } catch (fetchError) {
        if (!isCurrent()) return;
        const mapped = mapError(fetchError);
        setHasMoreHistory(false);
        if (mapped.code === "TOKEN_NOT_AVAILABLE") {
          markCandlesEmpty(key);
          setHistoricalUnavailable(true);
          return;
        }
        setError(mapped);
      } finally {
        if (isCurrent()) {
          setIsLoading(false);
        }
      }
    },
    [interval, pageSize, applyCandles]
  );

  const fetchInitial = useCallback(async () => {
    const generation = fetchGenerationRef.current + 1;
    fetchGenerationRef.current = generation;
    const isCurrent = () =>
      isMountedRef.current && fetchGenerationRef.current === generation;
    loadedCacheKeyRef.current = null;
    loadedIntervalRef.current = null;

    if (shouldEnable && liveExchange && liveSymbol) {
      await fetchLive(
        { exchange: liveExchange, symbol: liveSymbol },
        isCurrent
      );
      return;
    }

    if (!shouldEnable || !startTime || !endTime) {
      keptIntervalsRef.current = { key: "", byInterval: new Map() };
      setCandles([]);
      setError(null);
      setHistoricalUnavailable(false);
      setIsLoading(false);
      return;
    }

    const intervalMs = CANDLE_INTERVAL_MS[interval];
    const windowKey = [
      symbol,
      startTime,
      endTime,
      focusAnchorTime,
      focusEndTime,
      paddingCandles,
      initialFocusCandles,
      exchangeCandidatesKey,
      trustCandidates,
      anyVenue,
      allowRecentFallback,
    ].join("|");
    if (keptIntervalsRef.current.key !== windowKey) {
      keptIntervalsRef.current = { key: windowKey, byInterval: new Map() };
    }
    const kept = keptIntervalsRef.current.byInterval.get(interval);
    if (kept) {
      sourceRef.current = kept.source;
      setSource(kept.source);
      setCandles(kept.candles);
      setCandlesInterval(interval);
      earliestStartRef.current = kept.candles[0]?.startTime ?? null;
      latestStartRef.current =
        kept.candles[kept.candles.length - 1]?.startTime ?? null;
      loadedIntervalRef.current = interval;
      const latest = latestStartRef.current;
      setError(null);
      setHistoricalUnavailable(false);
      setHasMoreHistory(kept.hasMoreHistory);
      // Candles since the user left this interval load like any newer page.
      setHasMoreFuture(Boolean(latest && latest < Date.now() - intervalMs));
      setFollowLive(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setHistoricalUnavailable(false);
    setHasMoreHistory(true);
    setHasMoreFuture(true);
    setFollowLive(false);

    const { fetchStartTime, fetchEndTime } = getInitialFetchRange({
      startTime,
      endTime,
      interval,
      paddingCandles,
      focusAnchorTime,
      focusEndTime,
      initialFocusCandles,
    });

    const serviceInterval = toServiceInterval(interval);
    const rest = getPriceServiceRest();

    try {
      let markets: ServiceMarket[] | null = null;
      try {
        markets = await getPriceMarkets(symbol);
      } catch (lookupError) {
        if (isRateLimitError(lookupError)) {
          if (!isCurrent()) return;
          setCandles([]);
          setHasMoreHistory(false);
          setHasMoreFuture(false);
          setError({
            code: "RATE_LIMIT",
            message: "Rate limited — try again in a moment",
          });
          return;
        }
      }
      if (!isCurrent()) return;

      const resolution = await resolveCandleSource<CandleSource>({
        sources: toCandleSources(
          exchangeCandidatesRef.current,
          symbol,
          markets,
          trustCandidates,
          anyVenue
        ),
        intervalMs,
        startTime: fetchStartTime,
        endTime: fetchEndTime,
        allowRecentFallback,
        recentLimit: pageSize,
        fetchWindow: async (candidate, windowStart, windowEnd) => {
          const request = candleWindowRequest(windowStart, windowEnd, intervalMs);
          const fetched = await rest.getCandles({
            exchange: candidate.exchange,
            symbol: candidate.symbol,
            interval: serviceInterval,
            ...request,
          });
          return fetched.map(toCandlePoint);
        },
      });

      if (!isCurrent()) return;

      if (resolution.status === "candles") {
        sourceRef.current = resolution.source;
        setSource(resolution.source);
        setCandles(resolution.candles);
        setCandlesInterval(interval);
        loadedIntervalRef.current = interval;
        setHistoricalUnavailable(false);
        setHasMoreHistory(true);
        earliestStartRef.current = resolution.candles[0]?.startTime ?? null;
        latestStartRef.current =
          resolution.candles[resolution.candles.length - 1]?.startTime ?? null;

        const latest = latestStartRef.current;
        setHasMoreFuture(Boolean(latest && latest < Date.now() - intervalMs));
        return;
      }

      setCandles([]);
      setHasMoreHistory(false);
      setHasMoreFuture(false);
      if (resolution.status === "rate_limited") {
        setHistoricalUnavailable(false);
        setError({
          code: "RATE_LIMIT",
          message: "Rate limited — try again in a moment",
        });
        return;
      }
      setError(null);
      setHistoricalUnavailable(true);
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  }, [
    shouldEnable,
    startTime,
    endTime,
    symbol,
    interval,
    pageSize,
    paddingCandles,
    allowRecentFallback,
    trustCandidates,
    anyVenue,
    focusAnchorTime,
    focusEndTime,
    initialFocusCandles,
    exchangeCandidatesKey,
    liveExchange,
    liveSymbol,
    fetchLive,
  ]);

  const loadOlderCandles = useCallback(async () => {
    if (!shouldEnable || loadingMoreRef.current || !hasMoreHistory) return;

    const earliestStart = earliestStartRef.current;
    const candleSource = sourceRef.current;
    if (!earliestStart || !candleSource) return;
    const generation = fetchGenerationRef.current;

    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    setError(null);

    try {
      const fetched = await getPriceServiceRest().getCandles({
        exchange: candleSource.exchange,
        symbol: candleSource.symbol,
        interval: toServiceInterval(interval),
        limit: pageSize,
        end: Math.floor(earliestStart / 1000) - 1,
      });
      const older = fetched
        .map(toCandlePoint)
        .filter((candle) => candle.startTime < earliestStart);

      // A different market or interval was loaded meanwhile.
      if (!isMountedRef.current || fetchGenerationRef.current !== generation) {
        return;
      }
      if (older.length === 0) {
        setHasMoreHistory(false);
        return;
      }

      applyCandles(older);

      if (fetched.length < pageSize) {
        setHasMoreHistory(false);
      }
    } catch (fetchError) {
      if (!isMountedRef.current) return;
      if (mapError(fetchError).code === "HISTORY_OUT_OF_RANGE") {
        setHasMoreHistory(false);
      }
    } finally {
      loadingMoreRef.current = false;
      if (isMountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [shouldEnable, hasMoreHistory, interval, pageSize, applyCandles]);

  const loadNewerCandles = useCallback(async () => {
    if (!shouldEnable || loadingMoreRef.current || !hasMoreFuture) return;

    const latestStart = latestStartRef.current;
    const candleSource = sourceRef.current;
    if (!latestStart || !candleSource) return;

    const intervalMs = CANDLE_INTERVAL_MS[interval];
    const now = Date.now();
    const latestAllowed = now - intervalMs;
    if (latestStart >= latestAllowed) {
      setHasMoreFuture(false);
      return;
    }
    const generation = fetchGenerationRef.current;

    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    setError(null);

    try {
      const start = latestStart + intervalMs;
      const end = Math.min(start + intervalMs * pageSize, now);
      const fetched = await getPriceServiceRest().getCandles({
        exchange: candleSource.exchange,
        symbol: candleSource.symbol,
        interval: toServiceInterval(interval),
        ...candleWindowRequest(start, end, intervalMs, now),
      });
      const newer = fetched
        .map(toCandlePoint)
        .filter((candle) => candle.startTime > latestStart);

      // A different market or interval was loaded meanwhile.
      if (!isMountedRef.current || fetchGenerationRef.current !== generation) {
        return;
      }
      if (newer.length === 0) {
        setHasMoreFuture(false);
        return;
      }

      applyCandles(newer);

      const latestAfter = newer[newer.length - 1]?.startTime ?? latestStart;
      if (latestAfter >= latestAllowed || newer.length < pageSize) {
        setHasMoreFuture(false);
      }
    } catch {
      if (!isMountedRef.current) return;
    } finally {
      loadingMoreRef.current = false;
      if (isMountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [shouldEnable, hasMoreFuture, interval, pageSize, applyCandles]);

  useEffect(() => {
    isMountedRef.current = true;
    void fetchInitial();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchInitial]);

  // Live charts keep their market's history for the next open or interval switch.
  useEffect(() => {
    const key = loadedCacheKeyRef.current;
    if (!key || candles.length === 0) return;
    setCachedCandles(key, candles, hasMoreHistory);
  }, [candles, hasMoreHistory]);

  // Window charts keep each interval's candles while open.
  useEffect(() => {
    const loadedInterval = loadedIntervalRef.current;
    const candleSource = sourceRef.current;
    if (!loadedInterval || !candleSource || candles.length === 0) return;
    keptIntervalsRef.current.byInterval.set(loadedInterval, {
      candles,
      source: candleSource,
      hasMoreHistory,
    });
  }, [candles, hasMoreHistory]);

  const sourceExchange = source?.exchange ?? null;
  const sourceSymbol = source?.symbol ?? null;

  useEffect(() => {
    if (!shouldEnable || !followLive || !sourceExchange || !sourceSymbol) {
      return;
    }

    const serviceInterval = toServiceInterval(interval);
    if (isStreamedInterval(serviceInterval)) {
      return getPriceStream().subscribeCandles(
        sourceExchange,
        sourceSymbol,
        serviceInterval,
        { onCandle: (candle) => applyCandles([toCandlePoint(candle)]) }
      );
    }

    // The stream does not build this interval: refresh the latest candles.
    let stopped = false;
    const poll = () => {
      getPriceServiceRest()
        .getCandles({
          exchange: sourceExchange,
          symbol: sourceSymbol,
          interval: serviceInterval,
          limit: 2,
        })
        .then((latest) => {
          if (!stopped) applyCandles(latest.map(toCandlePoint));
        })
        .catch(() => undefined);
    };
    poll();
    const timer = setInterval(poll, LIVE_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [sourceExchange, sourceSymbol, interval, followLive, shouldEnable, applyCandles]);

  const priceChanges = useMemo(
    () => candles.map((candle) => toPriceChange(candle)),
    [candles]
  );
  // Right after an interval switch the state still holds the previous
  // interval's candles; never hand them out as the new interval's.
  const switchingInterval = candles.length > 0 && candlesInterval !== interval;

  return {
    priceChanges,
    isLoading: isLoading || switchingInterval,
    isLoadingMore,
    error,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
    historicalUnavailable,
  };
}
